// JQ4-45 · US-AUDIT-005 — AuditLogFilterBar
//
// Filter controls for the audit log: category, date range (from/to), actor userId.
// Spec refs: R-FILTER-01 to R-FILTER-06, R-LIST-06 (reset on filter change).

import { useTranslation } from '@jq4race/i18n';
import type { AuditCategory } from '@jq4race/shared';
import { Button, Input } from '@jq4race/ui';
import { Search, X } from 'lucide-react';
import { useState } from 'react';

import { useCategoryOptions } from '../../lib/categories';
import { isValidUuid } from '../../lib/isValidUuid';

export interface AuditLogFilters {
  category: AuditCategory | '';
  from: string;
  to: string;
  actorUserId: string;
}

interface AuditLogFilterBarProps {
  filters: AuditLogFilters;
  onFiltersChange: (filters: AuditLogFilters) => void;
}

export function AuditLogFilterBar({ filters, onFiltersChange }: AuditLogFilterBarProps) {
  const { t } = useTranslation('audit-log');
  const categoryOptions = useCategoryOptions();
  const [dateError, setDateError] = useState<string | null>(null);
  const [actorError, setActorError] = useState<string | null>(null);

  function handleCategoryChange(value: string) {
    onFiltersChange({ ...filters, category: value as AuditCategory | '' });
  }

  // R-FILTER-05: validate the date range on either date change before applying.
  function commitWithDateValidation(next: AuditLogFilters) {
    setDateError(null);
    if (next.from && next.to && new Date(next.from) > new Date(next.to)) {
      setDateError(t('filters.errors.dateRange'));
      return;
    }
    onFiltersChange(next);
  }

  function handleFromChange(value: string) {
    commitWithDateValidation({ ...filters, from: value });
  }

  function handleToChange(value: string) {
    commitWithDateValidation({ ...filters, to: value });
  }

  function handleActorChange(value: string) {
    // Surface a hint while the value is incomplete; the page only queries on a
    // valid UUID, so partial input never reaches the server.
    setActorError(value !== '' && !isValidUuid(value) ? t('filters.errors.actorFormat') : null);
    onFiltersChange({ ...filters, actorUserId: value });
  }

  function handleClear() {
    setDateError(null);
    setActorError(null);
    onFiltersChange({ category: '', from: '', to: '', actorUserId: '' });
  }

  const hasActiveFilters =
    filters.category !== '' ||
    filters.from !== '' ||
    filters.to !== '' ||
    filters.actorUserId !== '';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        {/* Category filter */}
        <div className="flex flex-col gap-1">
          <label
            htmlFor="audit-category"
            className="text-[11px] font-medium uppercase tracking-[0.18em] text-jq-graphite"
          >
            {t('filters.category.label')}
          </label>
          <select
            id="audit-category"
            value={filters.category}
            onChange={(e) => handleCategoryChange(e.target.value)}
            className="h-9 rounded-md border border-jq-sand/70 bg-jq-surface-0 px-3 text-[13px] text-jq-charcoal focus:border-jq-navy/40 focus:outline-none focus:ring-1 focus:ring-jq-navy/20"
          >
            <option value="">{t('filters.category.all')}</option>
            {categoryOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* From date */}
        <div className="flex flex-col gap-1">
          <label
            htmlFor="audit-from"
            className="text-[11px] font-medium uppercase tracking-[0.18em] text-jq-graphite"
          >
            {t('filters.from')}
          </label>
          <Input
            id="audit-from"
            type="date"
            value={filters.from}
            onChange={(e) => handleFromChange(e.target.value)}
            className="h-9 w-[160px] text-[13px]"
          />
        </div>

        {/* To date */}
        <div className="flex flex-col gap-1">
          <label
            htmlFor="audit-to"
            className="text-[11px] font-medium uppercase tracking-[0.18em] text-jq-graphite"
          >
            {t('filters.to')}
          </label>
          <Input
            id="audit-to"
            type="date"
            value={filters.to}
            onChange={(e) => handleToChange(e.target.value)}
            className="h-9 w-[160px] text-[13px]"
          />
        </div>

        {/* Actor userId (R1: super-admin actor picker is out of scope; org-admin uses free text) */}
        <div className="flex flex-col gap-1">
          <label
            htmlFor="audit-actor"
            className="text-[11px] font-medium uppercase tracking-[0.18em] text-jq-graphite"
          >
            {t('filters.actor.label')}
          </label>
          <div className="relative flex items-center">
            <Search className="absolute left-2.5 h-3.5 w-3.5 text-jq-stone" strokeWidth={1.5} />
            <Input
              id="audit-actor"
              type="text"
              placeholder={t('filters.actor.placeholder')}
              value={filters.actorUserId}
              onChange={(e) => handleActorChange(e.target.value)}
              className="h-9 w-[220px] pl-8 text-[13px]"
            />
          </div>
        </div>

        {/* Clear button */}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClear}
            className="h-9 text-[13px] text-jq-graphite"
          >
            <X className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
            {t('filters.clear')}
          </Button>
        )}
      </div>

      {/* Inline validation errors — R-FILTER-05 (date range) + actor UUID format */}
      {(dateError || actorError) && (
        <div className="space-y-1">
          {dateError && (
            <p role="alert" className="text-[12px] text-jq-danger">
              {dateError}
            </p>
          )}
          {actorError && (
            <p role="alert" className="text-[12px] text-jq-danger">
              {actorError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
